import React, { useState, useMemo, useCallback, useEffect } from 'react'

import moment from 'moment/min/moment-with-locales'

import { type DateObject, type EventItem } from '@/types'
import Button from '@components/Button'
import { MediaCard, type MediaCardProps } from '@components/MediaCard'
import { Section } from '@components/Section'
import { type SectionCTAProps } from '@components/Section/SectionCTA'
import Select, { type SelectValueProps } from '@components/Select'
import Swiper, { type SwiperOptions } from '@components/Swiper'
import { formatDate } from '@helpers/date'
import { trans, FROM, TO, THE, UNTIL, getLocale } from '@helpers/translator'

/**
 * Extension du type EventItem pour inclure les propriétés de MediaCardProps
 * en excluant les propriétés qui pourraient causer des conflits de type
 */
export interface Event
  extends Omit<
      MediaCardProps,
      'id' | 'title' | 'image' | 'url' | 'startDate' | 'endDate'
    >,
    EventItem {}

interface AgendaProps {
  title?: string
  description?: string
  events?: Event[]
  eventTypes?: SelectValueProps[]
  displayFilters?: boolean
  preLabel?: string
  cta?: SectionCTAProps
  parentUrl?: string
}

const Agenda: React.FC<AgendaProps> = ({
  title,
  description,
  events,
  eventTypes,
  displayFilters,
  preLabel,
  cta,
  parentUrl,
}) => {
  if (!events || events.length === 0) return null

  moment.locale(getLocale())

  const currentUrl = useMemo(() => {
    return cta?.url ? cta.url.href : parentUrl
  }, [cta?.url, parentUrl])

  const typeOptions = useMemo<SelectValueProps[]>(() => {
    if (!displayFilters || !eventTypes) return []
    return eventTypes ?? []
  }, [eventTypes, displayFilters])

  const [filters, setFilters] = useState({
    type: null as SelectValueProps | null,
  })

  const handleTypeChange = useCallback(
    (value: SelectValueProps | SelectValueProps[]) => {
      const selectedValue = Array.isArray(value) ? value[0] : value

      if (currentUrl && selectedValue && selectedValue?.value !== null) {
        const queryParams = new URLSearchParams()
        queryParams.set('event_type[]', selectedValue.value.toString())
        window.location.href = `${currentUrl}?${queryParams.toString()}`
      }
    },
    [currentUrl],
  )

  const mediaCardList = useMemo<React.ReactNode[]>(
    () =>
      events.map((event) => (
        <MediaCard
          title={event.title}
          id={event.id}
          image={event.image}
          url={event.url}
          metadata={formatDate(event.startDate, event.endDate)}
          size="lg"
          overlay={{
            position: 'bottom',
            overlayHeight: 'threeQuarter',
          }}
          className="h-[448px] lg:h-[600px] max-w-[440px]"
          titleClassName="mb-10 leading-7 lg:text-[35px] lg:leading-[40px] line-clamp-2"
          aspectRatio="aspect-[170/39] lg:aspect-[11/15]"
          tags={event.type}
          content_type="event"
          startDate={
            typeof event.startDate === 'string'
              ? event.startDate
              : event.startDate?.date
          }
          endDate={
            typeof event.endDate === 'string'
              ? event.endDate
              : event.endDate?.date
          }
        />
      )),
    [events],
  )

  const ctaCard = useMemo<React.ReactNode>(
    () =>
      cta?.url && (
        <div className="flex flex-col items-center justify-center h-[448px] lg:h-[600px] w-[260px] gap-y-16 px-10">
          {preLabel && <p className="text-[22px] text-balance">{preLabel}</p>}
          {cta.url && cta.label && (
            <Button
              icon={{
                name: 'arrow',
                background: 'red',
                color: 'white',
              }}
              href={cta.url.href}
              target={cta.url.target}
              ariaLabel={cta.ariaLabel || cta.label}
              className="self-start"
            >
              {cta.label}
            </Button>
          )}
        </div>
      ),
    [cta, preLabel],
  )

  let cardList: React.ReactNode[] = mediaCardList

  if (ctaCard) {
    cardList = [...cardList, ctaCard]
  }

  const swiperOptions = useMemo<SwiperOptions>(
    () => ({
      type: 'slide',
      autoWidth: true,
      gap: 0,
      omitEnd: true,
    }),
    [],
  )

  return (
    <Section>
      <Section.Content stickOnBorder>
        {title && <Section.Title title={title} />}
        <div className="flex flex-col lg:flex-row lg:items-center mt-2">
          {description && (
            <p className="text-base text-balance">{description}</p>
          )}
          {displayFilters && typeOptions?.length > 0 && currentUrl && (
            <div className="flex flex-col gap-y-5 mt-6 lg:mt-0 lg:flex-row lg:ml-auto lg:gap-x-4 ltr:lg:mr-7 rtl:lg:ml-7">
              {typeOptions?.length > 0 && (
                <Select
                  options={typeOptions}
                  value={filters.type}
                  onChange={handleTypeChange}
                />
              )}
            </div>
          )}
        </div>
        {cardList.length > 1 && (
          <Swiper
            className="mt-8"
            paginationClassnames="ltr:lg:ml-0 rtl:lg:mr-0 ltr:mr-7 rtl:ml-7"
            slides={cardList}
            options={swiperOptions}
          />
        )}
        {cardList.length === 1 && (
          <div className="mt-8 h-[448px] lg:h-[600px] max-w-[440px] aspect-[170/39] lg:aspect-[11/15]">
            {cardList[0]}
          </div>
        )}
      </Section.Content>
    </Section>
  )
}

export default Agenda
